import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminService } from "@/services/adminService";

export function useAdminStats() {
  return useQuery({ queryKey: ["admin", "stats"], queryFn: adminService.stats });
}

export function useBusinessOverview() {
  return useQuery({ queryKey: ["admin", "business-overview"], queryFn: adminService.businessOverview });
}

export function useAdminBulkJobs() {
  return useQuery({
    queryKey: ["admin", "bulk-jobs"],
    queryFn: adminService.bulkJobs,
    refetchInterval: (query) => {
      const jobs = query.state.data || [];
      return jobs.some((j) => j.status === "pending" || j.status === "running") ? 3000 : false;
    },
  });
}

export function useAdminUsers() {
  return useQuery({ queryKey: ["admin", "users"], queryFn: adminService.users });
}

export function useAdminCourses() {
  return useQuery({ queryKey: ["admin", "courses"], queryFn: adminService.courses });
}

export function useSupportCourses() {
  return useQuery({ queryKey: ["admin", "support-courses"], queryFn: adminService.supportCourses });
}

export function useToggleSupportCourse(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isSupport }) => adminService.toggleSupportCourse(id, isSupport),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "courses"] });
      qc.invalidateQueries({ queryKey: ["admin", "support-courses"] });
      options.onSuccess?.(...args);
    },
  });
}

export function useAdminTickets() {
  return useQuery({ queryKey: ["admin", "tickets"], queryFn: adminService.tickets });
}

export function useReplyTicket(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, message }) => adminService.reply(id, message),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "tickets"] });
      options.onSuccess?.(...args);
    },
  });
}

export function useSupportFaqs() {
  return useQuery({ queryKey: ["admin", "support-faqs"], queryFn: adminService.supportFaqs });
}

function useFaqMutation(mutationFn, options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "support-faqs"] });
      qc.invalidateQueries({ queryKey: ["admin", "tickets"] });
      options.onSuccess?.(...args);
    },
  });
}

export const useCreateSupportFaq = (o) => useFaqMutation(adminService.createSupportFaq, o);
export const useUpdateSupportFaq = (o) => useFaqMutation(({ id, ...body }) => adminService.updateSupportFaq(id, body), o);
export const useDeleteSupportFaq = (o) => useFaqMutation(adminService.deleteSupportFaq, o);

export function useTriageTicket(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...triage }) => adminService.triageTicket(id, triage),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "tickets"] });
      options.onSuccess?.(...args);
    },
  });
}

export function useGrantTicketCoins(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, delta }) => adminService.grantTicketCoins(id, delta),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "tickets"] });
      options.onSuccess?.(...args);
    },
  });
}

export function useTicketStatus(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }) => adminService.ticketStatus(id, status),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "tickets"] });
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
      options.onSuccess?.(...args);
    },
  });
}

export function useTicketNotes(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, analyst_notes }) => adminService.ticketNotes(id, analyst_notes),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "tickets"] });
      options.onSuccess?.(...args);
    },
  });
}

export function useAdjustWallet(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.adjustWallet,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      options.onSuccess?.(...args);
    },
  });
}

export function useAdminPublicPlaylist() {
  return useQuery({ queryKey: ["admin", "public-playlist"], queryFn: adminService.publicPlaylist });
}

function usePlaylistMutation(fn, options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "public-playlist"] });
      qc.invalidateQueries({ queryKey: ["admin", "courses"] });
      options.onSuccess?.(...args);
    },
  });
}

export const useAddToPlaylist = (o) => usePlaylistMutation(adminService.addToPlaylist, o);
export const useRemoveFromPlaylist = (o) => usePlaylistMutation(adminService.removeFromPlaylist, o);
export const useReorderPlaylist = (o) => usePlaylistMutation(adminService.reorderPlaylist, o);
export const useFeaturedGenerate = (o) => usePlaylistMutation(adminService.featuredGenerate, o);

export function useControlCenter() {
  return useQuery({
    queryKey: ["admin", "control-center"],
    queryFn: adminService.controlCenter,
    refetchInterval: 30000,
  });
}

export function useSetSystemStatus(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.setSystemStatus,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "control-center"] });
      options.onSuccess?.(...args);
    },
  });
}

export function useAlerts() {
  return useQuery({ queryKey: ["admin", "alerts"], queryFn: adminService.alerts, refetchInterval: 30000 });
}

export function useResolveAlert(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.resolveAlert,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "alerts"] });
      qc.invalidateQueries({ queryKey: ["admin", "control-center"] });
      options.onSuccess?.(...args);
    },
  });
}


export function useTransactions(params = {}) {
  return useQuery({ queryKey: ["admin", "transactions", params], queryFn: () => adminService.transactions(params) });
}

export function useRefund(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.refund,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "transactions"] });
      qc.invalidateQueries({ queryKey: ["admin", "control-center"] });
      options.onSuccess?.(...args);
    },
  });
}

export function useUserDetail(id, options = {}) {
  return useQuery({ queryKey: ["admin", "user", id], queryFn: () => adminService.userDetail(id), enabled: !!id, ...options });
}

function useUserMutation(fn, options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "user"] });
      options.onSuccess?.(...args);
    },
  });
}

export const useUpdateUser = (o) => useUserMutation(adminService.updateUser, o);
export const useDeleteUser = (o) => useUserMutation(adminService.deleteUser, o);
export const useResetPassword = (o) => useUserMutation(adminService.resetPassword, o);

export function useModerationLog() {
  return useQuery({ queryKey: ["admin", "moderation-log"], queryFn: adminService.moderationLog, refetchInterval: 30000 });
}

export function useDeleteCourse(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.deleteCourse,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "courses"] });
      qc.invalidateQueries({ queryKey: ["admin", "public-playlist"] });
      options.onSuccess?.(...args);
    },
  });
}


export function useNotifyUser(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.notifyUser, ...options,
    onSuccess: (...args) => { qc.invalidateQueries({ queryKey: ["admin", "comm-log"] }); options.onSuccess?.(...args); },
  });
}

export function useNotifyAll(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.notifyAll, ...options,
    onSuccess: (...args) => { qc.invalidateQueries({ queryKey: ["admin", "comm-log"] }); options.onSuccess?.(...args); },
  });
}

export function useCommLog() {
  return useQuery({ queryKey: ["admin", "comm-log"], queryFn: adminService.commLog, refetchInterval: 30000 });
}

export function useCommTemplates() {
  return useQuery({ queryKey: ["admin", "comm-templates"], queryFn: adminService.listTemplates });
}

export function useCreateTemplate(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.createTemplate, ...options,
    onSuccess: (...args) => { qc.invalidateQueries({ queryKey: ["admin", "comm-templates"] }); options.onSuccess?.(...args); },
  });
}

export function useDeleteTemplate(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.deleteTemplate, ...options,
    onSuccess: (...args) => { qc.invalidateQueries({ queryKey: ["admin", "comm-templates"] }); options.onSuccess?.(...args); },
  });
}

export function useAdminFeedback(params = {}) {
  return useQuery({
    queryKey: ["admin", "feedback", params],
    queryFn: () => adminService.feedback(params),
    refetchInterval: 30000,
  });
}

export function useResolveFeedback(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.resolveFeedback, ...options,
    onSuccess: (...args) => { qc.invalidateQueries({ queryKey: ["admin", "feedback"] }); options.onSuccess?.(...args); },
  });
}

export function useUpdateFeedback(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.updateFeedback, ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "feedback"] });
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
      qc.invalidateQueries({ queryKey: ["admin", "feedback-digest"] });
      options.onSuccess?.(...args);
    },
  });
}

export function useRejectFeedback(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.rejectFeedback, ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "feedback"] });
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
      qc.invalidateQueries({ queryKey: ["admin", "feedback-digest"] });
      options.onSuccess?.(...args);
    },
  });
}

export function useRestoreFeedback(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.restoreFeedback, ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "feedback"] });
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
      options.onSuccess?.(...args);
    },
  });
}

export function useFeedbackDigest() {
  return useQuery({ queryKey: ["admin", "feedback-digest"], queryFn: adminService.feedbackDigest, refetchInterval: 60000 });
}

export function useWipLimits() {
  return useQuery({ queryKey: ["admin", "wip-limits"], queryFn: adminService.getWipLimits });
}

export function useUpdateWipLimits(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.updateWipLimits, ...options,
    onSuccess: (...args) => { qc.invalidateQueries({ queryKey: ["admin", "wip-limits"] }); options.onSuccess?.(...args); },
  });
}

export function useReplyFeedback(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.replyFeedback, ...options,
    onSuccess: (...args) => { qc.invalidateQueries({ queryKey: ["admin", "feedback"] }); options.onSuccess?.(...args); },
  });
}

export function useInviteTester(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminService.inviteTester, ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "user"] });
      options.onSuccess?.(...args);
    },
  });
}
